/**
 * byo-v4-bar.js — the sticky build bar and its parts (P4).
 *
 * Ported by name from the design so a reviewer can diff component for
 * component: `DispatchLine`, `ItemStrip`, `SpaceMeter`, `TrayMeter` from
 * `byo-core-v2.jsx:159-207`, and `ShippingBar` + `BuildBar` from
 * `byo-stages-v3.jsx:37-159`. DESIGN-SPEC.md §3.4, §3.4.1, §3.4.2.
 *
 * Two mechanical differences from the JSX, neither of them a design change:
 *
 * 1. The design branches on a `wide` prop and returns three whole bars
 *    (desktop, phone-collapsed, phone-open). This renders the phone shapes and
 *    lets CSS promote the bar to the desktop row at 990px, as everything else
 *    in this build does. The collapsed/open split stays in JS, because it is
 *    state (a 2.4s peek), not a breakpoint.
 * 2. `b.units` here is a flat array of products (byo-v4-core.js:347), not the
 *    design's `{key, p}`. `unitsOf()` puts the key back so the strip can
 *    animate each cell independently.
 *
 * The keyframes the design injects from JS (byo-ship-*, byo-tip-*,
 * byo-slot-in) live in byo-v4-builder.css instead — no runtime <style> writes.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, useState, useEffect, useRef, useMemo } from './byo-v4-vendor.js';
const { Icon, Btn, Mark, ProductImage, MeterText } = (window.BYOV4 = window.BYOV4 || {});
/* Counts as words (D70), read at call time from byo-v4-core.js. */
const w = (n) => (window.BYO && window.BYO.words ? window.BYO.words(n) : String(n));
const W = (n) => (window.BYO && window.BYO.Words ? window.BYO.Words(n) : String(n));
/**
 * `wide` — byo-stages-v3.jsx:80 branches the bar on it, and the collapsed shape
 * is phone-only: `open = wide || stage !== 'fill' || pin || peek`. CSS alone
 * cannot express that, because collapsed/open is state (a 2.4s peek) as well as
 * a breakpoint, so the breakpoint is read here and the two are combined.
 */
function useWide() {
  const query = '(min-width: 990px)';
  const [wide, setWide] = useState(() => (window.matchMedia ? window.matchMedia(query).matches : false));

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = (e) => setWide(e.matches);
    setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  return wide;
}

/** The design's `{key, p}` units, rebuilt from our flat product array. */
export function unitsOf(b) {
  return (b.units || []).map((p, i) => ({ key: `${p.id}-${i}`, p }));
}

/**
 * DispatchLine — byo-core-v2.jsx:159, now the theme's own dispatch promise
 * (D70). The design counted to a noon cutoff and otherwise said "ships
 * tomorrow", whatever day tomorrow was: on a Saturday afternoon it counted
 * down 21 hours to a Sunday dispatch that never happens. The rest of the site
 * already has the right rules in snippets/dispatch-promise.liquid and
 * assets/dispatch-promise.js — no weekend dispatch, the shop's non-dispatch
 * dates, Friday afternoon to Saturday morning, and a clock only when the
 * cutoff is today, the day named otherwise — so the bar shows that element.
 * The section renders it into <template data-byo-v4-dispatch>, and the site
 * and the builder can never disagree about when a box ships.
 *
 * With no template (the offline harness, or a cached section) the same states
 * are worked out here in local time, without the non-dispatch dates.
 */
export function DispatchLine({ cutoff = '12:00' }) {
  const markup = useMemo(() => {
    const t = document.querySelector('template[data-byo-v4-dispatch]');
    return t ? t.innerHTML.trim() : '';
  }, []);

  if (!markup) return html`<${DispatchFallback} cutoff=${cutoff} />`;

  /* The same string on every render, so Preact never touches what the
     element writes into itself as it ticks. */
  return html`
    <div class="byo-v4-dispatch">
      <${Icon} name="clock" size=${13} stroke=${2.2} />
      <span class="byo-v4-dispatch__promise" dangerouslySetInnerHTML=${{ __html: markup }}></span>
    </div>
  `;
}

/** The theme's four states, for a page without the theme's element. */
function DispatchFallback({ cutoff }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const [hh, mm] = String(cutoff).split(':').map((n) => parseInt(n, 10) || 0);
  const d = new Date(now);
  const day = d.getDay();
  const left = hh * 3600 + mm * 60 - (d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds());

  if (day >= 1 && day <= 5 && left > 0) {
    const mins = Math.ceil(left / 60);
    const h = Math.floor(mins / 60);
    return html`
      <div class="byo-v4-dispatch">
        <${Icon} name="clock" size=${13} stroke=${2.2} />
        <span>Order in </span>
        <${Mark}>${h ? `${h}h ` : ''}${mins % 60}m<//>
        <span> and it ships today with Express</span>
      </div>
    `;
  }

  const when = day === 5 ? 'Saturday morning' : day === 6 || day === 0 ? 'Monday' : 'tomorrow';
  return html`
    <div class="byo-v4-dispatch">
      <${Icon} name="clock" size=${13} stroke=${2.2} />
      <span>Order now and it ships ${when} with Express</span>
    </div>
  `;
}

/**
 * ItemStrip — byo-core-v2.jsx:167. Items mode: thumbnails of what is in the
 * box, then the dashed, numbered cells of what is still needed for the
 * minimum. No spaces are ever shown, which is what keeps the word out of the
 * UI (P3 exit criterion).
 *
 * D71 (Alan, 26 Sep 2026): the strip ends in "+" cells for as long as more
 * will fit. Before, it drew exactly the minimum, so three items filled three
 * cells beside a "Ready to check out" pill and the box looked full — "it's
 * going to drive quite low AOV because it looks like the customer can't add
 * any more in". The "+" cells count the room left in the smallest items that
 * fit, so they go down faster when a big thing goes in, and they are gone only
 * when the box really is full. At least one shows whenever anything fits.
 */
export function ItemStrip({ b, height = 36, gap = 4, radius = 8, onClick, max = 8, class: cls = '' }) {
  const units = unitsOf(b);
  const size = Math.min(44, Math.max(28, Math.round(height * 1.4)));
  const need = Math.max(0, b.minItems - b.itemCount);

  /* Room left, in the smallest items that still fit. */
  const small = typeof b.slotsOf === 'function' ? b.slotsOf('Small') || 1 : 1;
  const fitsMore = typeof b.fitsSize === 'function' ? b.fitsSize('Small') : b.remaining > 1e-9;
  const roomSmalls = fitsMore ? Math.max(1, Math.floor((b.remaining + 1e-9) / small)) : 0;
  const optional = Math.max(0, roomSmalls - need);

  /* The cells, in priority order: one "+" whenever there is room, then what
     is still needed, then the thumbnails (the newest kept when there is space
     for only one, or "+N" for the rest), then more "+" to fill the strip. */
  const plus = optional > 0 ? 1 : 0;
  const needShown = Math.min(need, Math.max(0, max - plus));
  const thumbRoom = Math.max(0, max - plus - needShown);
  let thumbs = units;
  let hidden = 0;
  if (units.length > thumbRoom) {
    if (thumbRoom >= 2) {
      thumbs = units.slice(0, thumbRoom - 1);
      hidden = units.length - thumbs.length;
    } else {
      thumbs = thumbRoom ? units.slice(-1) : [];
    }
  }
  const used = thumbs.length + (hidden ? 1 : 0) + needShown;
  const rooms = plus ? Math.max(1, Math.min(optional, max - used)) : 0;
  const cells = used + rooms;

  const style = `grid-template-columns: repeat(${cells}, minmax(0, ${size}px)); gap: ${gap}px;`;
  const plusSize = Math.max(10, Math.round(size * 0.42));

  return html`
    <div
      class=${`byo-v4-strip ${cls}`.trim()}
      style=${style}
      onClick=${onClick}
      role=${onClick ? 'button' : undefined}
      tabIndex=${onClick ? 0 : undefined}
      onKeyDown=${onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(e);
            }
          }
        : undefined}
      aria-label=${onClick ? "See what's inside your box" : undefined}
    >
      ${thumbs.map(
        (u) => html`<div
          key=${u.key}
          class="byo-v4-strip__cell"
          style=${`border-radius:${radius}px`}
          title=${u.p.title}
        >
          <${ProductImage} p=${u.p} fit="contain" radius=${radius - 2} />
        </div>`
      )}
      ${hidden > 0 &&
      html`<div class="byo-v4-strip__more" style=${`border-radius:${radius}px`}>+${hidden}</div>`}
      ${Array.from({ length: needShown }, (_, i) => html`<div
          key=${`n${i}`}
          class="byo-v4-strip__need"
          style=${`border-radius:${radius}px`}
          title="Still needed"
        >
          ${b.itemCount + i + 1}
        </div>`)}
      ${Array.from({ length: rooms }, (_, i) => html`<div
          key=${`r${i}`}
          class="byo-v4-strip__room"
          style=${`border-radius:${radius}px`}
          title="Room for more"
        >
          <${Icon} name="plus" size=${plusSize} stroke=${2.5} />
        </div>`)}
    </div>
  `;
}

/**
 * SpaceMeter — byo-core-v2.jsx:181. The spaces-mode visualiser, kept because
 * `countUi` is a merchant setting (CONFIG-SCHEMA.md §3) and switching it must
 * not leave the bar without a tray. The placement algorithm is the design's,
 * verbatim: an item takes a run of whole cells inside one row, and only splits
 * across rows when no row has room (the continuation draws lighter).
 */
export function SpaceMeter({ b, height = 40, rows = 2, gap = 3, radius = 8, onClick, class: cls = '' }) {
  const units = unitsOf(b);
  const cap = Math.round(b.capacity);
  const cols = Math.ceil(cap / rows);
  const minN = Math.round(b.minimum || 0);
  const big = height >= 32;

  const cells = Array(cap).fill(null);
  const segs = [];

  const place = (u, n) => {
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c + n <= cols; c += 1) {
        const p = r * cols + c;
        if (p + n > cap) break;
        let ok = true;
        for (let k = 0; k < n; k += 1) if (cells[p + k]) { ok = false; break; }
        if (ok) {
          for (let k = 0; k < n; k += 1) cells[p + k] = u;
          segs.push({ u, r, c, len: n, first: true });
          return;
        }
      }
    }
    let left = n;
    let first = true;
    for (let p = 0; p < cap && left > 0; p += 1) {
      if (cells[p]) continue;
      const row = Math.floor(p / cols);
      let q = p;
      let len = 0;
      while (q < cap && left > 0 && !cells[q] && Math.floor(q / cols) === row) {
        cells[q] = u;
        q += 1;
        len += 1;
        left -= 1;
      }
      segs.push({ u, r: row, c: p % cols, len, first });
      first = false;
      p = q - 1;
    }
  };

  units.forEach((u) => place(u, Math.round(b.slotsOf(u.p.size))));

  let needLeft = Math.max(0, minN - Math.round(b.slotsUsed));
  const at = (r, c, len) => `grid-row:${r + 1}; grid-column:${c + 1} / span ${len};`;

  return html`
    <div class=${cls}>
      <div
        class=${`byo-v4-spaces${b.meetsMin ? ' is-met' : ''}`}
        style=${`grid-template-columns: repeat(${cols}, minmax(0,1fr)); grid-template-rows: repeat(${rows}, ${height}px); gap:${gap}px; border-radius:${radius + 2}px;`}
        onClick=${onClick}
      >
        ${segs.map((s, i) => {
          const ghost = !s.first;
          const label = big && s.len >= 2 ? `${s.u.p.size} · ${s.len}` : '';
          return html`
            <div
              key=${`${s.u.key}-${i}`}
              class=${`byo-v4-spaces__seg${ghost ? ' is-ghost' : ''}`}
              style=${`${at(s.r, s.c, s.len)} border-radius:${radius}px;`}
              title=${`${s.u.p.title} · ${b.slotWord(s.u.p.size)}`}
            >
              <${Icon} name="check" size=${big ? 14 : 9} stroke=${3} />
              ${label && html`<span class="byo-v4-spaces__label">${label}</span>`}
            </div>
          `;
        })}
        ${cells.map((u, p) => {
          if (u) return null;
          const need = needLeft > 0;
          if (need) needLeft -= 1;
          return html`<div
            key=${`e${p}`}
            class=${`byo-v4-spaces__empty${need ? ' is-need' : ''}`}
            style=${`${at(Math.floor(p / cols), p % cols, 1)} border-radius:${radius}px;`}
          >
            ${big ? p + 1 : ''}
          </div>`;
        })}
      </div>
    </div>
  `;
}

/** TrayMeter — byo-core-v2.jsx:177. Items mode gets the strip, spaces the grid. */
export function TrayMeter(props) {
  return props.b.itemsMode ? html`<${ItemStrip} ...${props} />` : html`<${SpaceMeter} ...${props} />`;
}

/**
 * ShippingBar — byo-stages-v3.jsx:37. DESIGN-SPEC.md §3.4.2.
 *
 * Free delivery is tracked **per parcel** while building, so `subject` stays
 * "box" here; the cart (P7) is what totals an order. Crossing the threshold
 * plays the one-off celebration: badge pop, two rings, a shine across the fill
 * and a wiggle on the truck.
 */
export function ShippingBar({ b, compact, subject = 'box', class: cls = '' }) {
  const done = b.toFree <= 0;
  const pct = Math.min(1, b.freeShipping ? b.shipTotal / b.freeShipping : 0);
  const banner = b.shipStyle === 'banner';
  const rider = b.shipIcon === 'rider';
  const perBox = subject === 'box';

  const [party, setParty] = useState(0);
  const was = useRef(done);

  useEffect(() => {
    const fire = done && !was.current;
    was.current = done;
    if (!fire) return undefined;
    setParty((p) => p + 1);
    const t = setTimeout(() => setParty(0), 1800);
    return () => clearTimeout(t);
  }, [done]);

  return html`
    <div class=${cls}>
      <div class=${`byo-v4-ship${banner ? ' is-banner' : ''}${done ? ' is-done' : ''}`}>
        <div class="byo-v4-ship__line">
          <span class="byo-v4-ship__say">
            ${done
              ? html`<${Icon} name="check" size=${13} stroke=${3} /><span
                    >${perBox ? 'This box ships free' : 'Free shipping unlocked'}</span
                  >`
              : html`<span
                  ><strong>${b.money(b.toFree)}</strong>${perBox
                    ? ' more and this box ships free'
                    : ' away from free shipping'}</span
                >`}
          </span>
          ${!done &&
          !compact &&
          html`<span class="byo-v4-ship__of">${b.money(b.shipTotal)} / ${b.money(b.freeShipping)}</span>`}
        </div>

        <div class="byo-v4-ship__trackwrap">
          <div class="byo-v4-ship__track">
            <div class="byo-v4-ship__fill" style=${`width:${(pct * 100).toFixed(2)}%`}></div>
            ${party > 0 && html`<div key=${party} class="byo-v4-ship__shine"></div>`}
          </div>

          ${rider &&
          html`<span
            class=${`byo-v4-ship__rider${party ? ' is-driving' : ''}`}
            style=${`left: calc(10px + (100% - 40px) * ${pct.toFixed(4)})`}
          >
            <${Icon} name="truck" size=${12} stroke=${2.2} />
          </span>`}

          <span key=${party} class=${`byo-v4-ship__goal${party ? ' is-popping' : ''}`}>
            ${party > 0 &&
            html`<span class="byo-v4-ship__ring byo-v4-ship__ring--a"></span
              ><span class="byo-v4-ship__ring byo-v4-ship__ring--b"></span>`}
            ${rider
              ? html`<${Icon} name=${done ? 'check' : 'home'} size=${13} stroke=${done ? 3 : 2.2} />`
              : html`<${Icon} name="truck" size=${13} stroke=${2.2} />`}
          </span>
        </div>
      </div>
    </div>
  `;
}

/**
 * BuildBar — byo-stages-v3.jsx:71. DESIGN-SPEC.md §3.4.
 *
 * Two heights on the phone. Collapsed while browsing Fill; it opens for 2.4s
 * whenever `units` changes so the item is seen landing, on tapping the price,
 * and permanently on every stage after Fill. `pin` resets on every stage
 * change. Desktop is always open — CSS promotes the open shape to the 1200px
 * row, so `wide` is not a prop here.
 *
 * The tray carries `[data-byo-fly-target]`, which is what `flyToBox` (P3b) has
 * been arcing towards since it was written.
 */
export function BuildBar({ b, config, stage = 'fill', tray = true, back, onBack, next, onNext, nextDisabled, hint, onTray, onHelp }) {
  const units = b.units || [];
  const empty = !units.length;
  const ledger = (config.priceUi || 'ledger') === 'ledger';
  const base = config.base || 0;
  const sub = ledger && !empty ? `${window.BYO.moneyShort(base)} box + ${b.money(b.total - base)} added` : null;

  const [pin, setPin] = useState(false);
  const [peek, setPeek] = useState(false);

  const sig = unitsOf(b)
    .map((u) => u.key)
    .join(',');
  const prevSig = useRef(sig);

  useEffect(() => {
    if (prevSig.current === sig) return undefined;
    prevSig.current = sig;
    setPeek(true);
    const t = setTimeout(() => setPeek(false), 2400);
    return () => clearTimeout(t);
  }, [sig]);

  useEffect(() => {
    setPin(false);
  }, [stage]);

  const wide = useWide();
  /* The design is `open = wide || stage !== 'fill' || pin || peek`
     (byo-stages-v3.jsx:80). Alan's call: past Fill the open bar carries tray
     detail nobody needs, so only Fill on desktop opens by default. Everywhere
     else it is the thin bar — countdown, delivery strip, price and the chevron
     that opens What's Inside. DEVIATIONS.md D34. */
  const open = (wide && stage === 'fill') || pin || peek;

  /* Coach tip: once per visit, on the SECOND add (the first is busy playing the
     fly-in), only while collapsed on Fill and only if the bar has never been
     opened. byo-stages-v3.jsx:83, audit #8. */
  const [tip, setTip] = useState(false);
  const tipDone = useRef(false);
  const opened = useRef(false);

  useEffect(() => {
    /* Never on desktop: the bar is already open there. byo-stages-v3.jsx:84. */
    if (wide || open || stage !== 'fill' || units.length < 2 || opened.current || tipDone.current) return undefined;
    tipDone.current = true;
    setTip(true);
    const t = setTimeout(() => setTip(false), 7000);
    return () => clearTimeout(t);
  }, [open, wide, stage, units.length]);

  const expand = () => {
    setTip(false);
    opened.current = true;
    if (onTray) onTray();
    else setPin(true);
  };

  const touchTag = stage === 'fill' && b.touchCount > 0 ? ` · ${w(b.touchCount)} touch${b.touchCount > 1 ? 'es' : ''}` : '';
  const dotTone = !b.meetsMin ? 'orange' : b.remaining <= 1e-9 ? 'plum' : 'lime';
  const status =
    (!b.meetsMin
      ? b.itemsMode
        ? b.itemCount
          ? `${W(b.itemCount)} of ${w(b.minItems)} items · ${w(b.need)} more needed`
          : `No items yet · ${w(b.need)} needed`
        : `${window.BYO.fmtSlots(b.need)} more needed`
      : b.remaining <= 1e-9
        ? 'Full · ready'
        : b.itemsMode
          ? `${W(b.itemCount)} items · room for more`
          : `Ready · ${window.BYO.fmtSlots(b.remaining)} spare`) + touchTag;

  /* The collapsed phone bar's line, beside the price. Counts in words (D70)
     made the long form too wide for it ("Four item…"), so it keeps only what
     that spot needs: how many more, or how many in. The tray beside it shows
     the rest, and the open bar and the rail keep the full sentence. */
  const statusShort =
    (!b.meetsMin
      ? b.itemsMode
        ? `${W(b.need)} more needed`
        : `${window.BYO.fmtSlots(b.need)} more needed`
      : b.remaining <= 1e-9
        ? 'Full · ready'
        : b.itemsMode
          ? 'Room for more'
          : `Ready · ${window.BYO.fmtSlots(b.remaining)} spare`) + touchTag;

  /* Third line only for blockers the status line does not already carry. */
  const third =
    nextDisabled && stage !== 'fill'
      ? (stage === 'review' ? b.blockers.filter((x) => !/more (space|item)/.test(x)).join(' · ') : hint) || null
      : null;

  /* A Help pill with no sheet behind it, or a tray chevron with nothing to
     open, is a control that does nothing — worse than none, which is the same
     call P3a made about the card image. Both appear with their sheet. */
  const helpBtn = !onHelp
    ? null
    : html`
    <button class="byo-v4-bar__help" onClick=${onHelp} aria-label="Help" title="Help">
      <span class="byo-v4-bar__helpring" aria-hidden="true"></span>
      <${Icon} name="help" size=${20} />
      <span class="byo-v4-bar__helpword">Help</span>
    </button>
  `;

  const buttons = html`
    ${back && html`<${Btn} variant="ghost" size="sm" onClick=${onBack} class="byo-v4-bar__back"
        ><${Icon} name="chevL" size=${18} />Back<//
      >`}
    ${next && html`<${Btn} size="md" onClick=${onNext} disabled=${nextDisabled} icon="arrowR">${next}<//>`}
  `;

  const trayBlock = tray
    ? html`<div class="byo-v4-bar__tray" data-byo-fly-target>
        ${onTray &&
        html`<button class="byo-v4-bar__trayup" onClick=${onTray} aria-label="See what's inside your box" title="What's inside">
          <${Icon} name="chevU" size=${18} stroke=${2.5} />
        </button>`}
        <${TrayMeter} b=${b} height=${22} gap=${2} radius=${6} max=${6} onClick=${onTray} class="byo-v4-bar__traymeter" />
        ${stage === 'fill' &&
        html`<button
          class="byo-v4-bar__traydown"
          onClick=${() => {
            setPin(false);
            setPeek(false);
          }}
          aria-label="Hide box details"
        >
          <${Icon} name="chevD" size=${18} stroke=${2.5} />
        </button>`}
      </div>`
    : null;

  return html`
    <div class=${`byo-v4-bar${open ? ' is-open' : ' is-collapsed'}`} data-byo-bar data-stage=${stage}>
      <${DispatchLine} cutoff=${config.cutoff} />

      ${!open &&
      html`
        ${b.toFree <= 0 || (b.shipTotal > 0 && b.toFree <= 2000)
          ? html`<div class="byo-v4-bar__strip" data-screen-label="Free delivery strip">
              <div
                class=${`byo-v4-bar__stripfill${b.toFree <= 0 ? ' is-done' : ''}`}
                style=${`width:${(Math.min(1, b.freeShipping ? b.shipTotal / b.freeShipping : 0) * 100).toFixed(1)}%`}
              ></div>
              <div class=${`byo-v4-bar__striptext${b.toFree <= 0 ? ' is-done' : ''}`}>
                ${b.toFree <= 0
                  ? 'This box ships free'
                  : `${b.money(b.toFree)} more and this box ships free`}
              </div>
            </div>`
          : html`<div class="byo-v4-bar__hair" title=${`${b.money(b.toFree)} more and this box ships free`}>
              <div
                class="byo-v4-bar__hairfill"
                style=${`width:${(Math.min(1, b.freeShipping ? b.shipTotal / b.freeShipping : 0) * 100).toFixed(1)}%`}
              ></div>
            </div>`}

        <div class="byo-v4-bar__row byo-v4-bar__row--collapsed">
          ${tray &&
          html`<div class="byo-v4-bar__minitray" data-byo-fly-target>
            ${b.itemsMode
              ? html`<${ItemStrip} b=${b} height=${20} gap=${3} radius=${6} max=${2} onClick=${onTray} class="byo-v4-bar__ministrip" />`
              : html`<${SpaceMeter} b=${b} height=${15} gap=${1.5} radius=${3} onClick=${onTray} class="byo-v4-bar__minispaces" />`}
          </div>`}

          <button class="byo-v4-bar__expand" onClick=${expand} aria-label="Show box details">
            <span class="byo-v4-bar__chevwrap">
              ${tip &&
              html`<span class="byo-v4-bar__tipring byo-v4-bar__tipring--a" aria-hidden="true"></span
                ><span class="byo-v4-bar__tipring byo-v4-bar__tipring--b" aria-hidden="true"></span>`}
              <span class=${`byo-v4-bar__chev${tip ? ' is-bobbing' : ''}`}>
                <${Icon} name="chevU" size=${16} stroke=${2.5} />
              </span>
              ${tip &&
              html`<span class="byo-v4-bar__tip" role="status">
                <span class="byo-v4-bar__tiptail" aria-hidden="true"></span>
                <strong>Your box lives down here.</strong> Tap to see what’s inside.
              </span>`}
            </span>
            <span class="byo-v4-bar__expandtext">
              <span class="byo-v4-bar__price">${b.money(b.total)}</span>
              <span class="byo-v4-bar__status">${statusShort}</span>
            </span>
          </button>

          ${helpBtn}
          ${/* The design's collapsed bar is Fill-only and so has no Back. It
               now runs on every stage, and Personalise and Confirm must still
               be able to step back. */ ''}
          ${back &&
          html`<${Btn} variant="ghost" size="sm" onClick=${onBack} class="byo-v4-bar__back"
            ><${Icon} name="chevL" size=${18} />Back<//
          >`}
          ${next && html`<${Btn} size="md" onClick=${onNext} disabled=${nextDisabled} icon="arrowR">${next}<//>`}
        </div>
      `}

      ${open &&
      html`
        <div class="byo-v4-bar__open">
          <${ShippingBar} b=${b} compact class="byo-v4-bar__ship" />
          ${trayBlock}

          <div class="byo-v4-bar__row byo-v4-bar__row--open">
            ${helpBtn}

            <div class="byo-v4-bar__totals">
              ${ledger &&
              html`<div class="byo-v4-bar__ledger">${empty ? 'Box from' : 'Your box'}</div>`}
              <div class="byo-v4-bar__totalrow">
                <span class="byo-v4-bar__total">
                  ${!ledger && empty && html`<span class="byo-v4-bar__from">From</span>`}${b.money(b.total)}
                </span>
                <${MeterText} b=${b} short class="byo-v4-bar__meter" />
              </div>
              <div class="byo-v4-bar__phonestatus">
                <span class=${`byo-v4-bar__dot byo-v4-bar__dot--${dotTone}`}></span>
                <span>${status}</span>
              </div>
              ${/* The ledger sub-line and the hint are gone — DEVIATIONS.md D35.
                   The price is already on screen, its breakdown is one tap away
                   in What's Inside, and the meter pill beside the total says
                   "2 more to check out" / "Full". A third phrasing of the same
                   fact is noise. D31 removed the "Box full" half of it; this
                   removes the line. */ ''}
              ${third && html`<div class="byo-v4-bar__third">${third}</div>`}
            </div>

            ${buttons}
          </div>
        </div>
      `}
    </div>
  `;
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { BuildBar, DispatchLine, ItemStrip, ShippingBar, SpaceMeter, TrayMeter, unitsOf });
